import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import axios from 'axios';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Video } from '../models/video.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class VideoService {
  http = inject(HttpClient);
  utilsService = inject(UtilsService);
  constructor() {}

  async getVideos(uid: string): Promise<Observable<any>> {
    try {
      const baseParams = new HttpParams().set('uid', uid).set('limit', 3);
      return this.utilsService.setHttpHeaders(false).then((headers) => {
        return this.http.get(environment['AWS_VIDEO_FETCH']!, {
          params: baseParams,
          headers: headers,
        });
      });
    } catch (error) {
      console.error(`Error fetching videos: ${error}`);
      throw { message: `Error fetching videos: ${error}` };
    }
  }

  async getVideoById(id: string): Promise<any> {
    try {
      return this.utilsService.setAxiosHeaders(false).then(async (headers) => {
        const response = await axios.get(
          environment['AWS_VIDEO_FETCH_BY_ID']!,
          {
            params: { id: id, limit: 1 },
            headers: headers,
          }
        );
        return response.data as Video;
      });
    } catch (error) {
      console.error(`Error fetching videos: ${error}`);
      throw { message: `Error fetching videos: ${error}` };
    }
  }

  async getVideoUploadUrl(uuid: string, fileExtn: string): Promise<any> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        headers = { ...headers, 'Content-Type': `video/${fileExtn}` };
        return axios.get<any>(environment['AWS_VIDEO_SIGNEDURL']!, {
          params: { id: uuid, fileExtn: fileExtn },
          headers: headers,
        });
      })
      .then((response) => {
        return {
          method: 'PUT',
          url: response.data.url,
          headers: {
            'Content-Type': `video/${fileExtn}`,
          },
        };
      });
  }

  // Call AWS API to Initilize Multipart Video upload
  async initializeMultipartUpload(): Promise<any> {
    return this.utilsService.setAxiosHeaders(true).then(async (headers) => {
      const response = await axios.post(
        environment['AWS_VIDEO_UPLOAD_INITIALIZE']!
      );
      return response.data;
    });
  }

  // Call AWS API to get PreSigned URL for uploading Video Part
  async getMultiPartPreSignedUrl(
    uploadId: string,
    key: string,
    partNumber: number
  ): Promise<string> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        return axios.get<any>(environment['AWS_VIDEO_UPLOAD_PART']!, {
          params: { uploadId: uploadId, key: key, partNumber: partNumber },
          headers: headers,
        });
      })
      .then((response) => response.data.url);
  }

  async listParts(uploadId: string, key: string): Promise<any> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        return axios.get<any>(environment['AWS_VIDEO_UPLOAD_LIST_PARTS']!, {
          params: { uploadId: uploadId, key: key },
          headers: headers,
        });
      })
      .then((response) => response.data['Parts']);
  }

  async abortMultipartUpload(uploadId: string, key: string) {
    const headers = await this.utilsService.setAxiosHeaders(true);
    const response = await axios.delete<any>(
      environment['AWS_VIDEO_UPLOAD_ABORT']!,
      {
        params: { uploadId: uploadId, key: key },
        headers: headers,
      }
    );
    return response.data;
  }

  async completeMultipartUpload(uploadId: string, key: string, parts: any) {
    return this.utilsService.setAxiosHeaders(true).then(async (headers) => {
      const response = await axios.post(
        environment['AWS_VIDEO_UPLOAD_COMPLETE']!,
        {
          uploadId: uploadId,
          key: key,
          parts: parts,
          headers: headers,
        }
      );
      return response.data;
    });
  }

  async create(video: Partial<Video>): Promise<any> {
    return this.utilsService.setAxiosHeaders(true).then(async (headers) => {
      // console.log('VideoService:: create:', headers);
      return axios
        .put(environment['AWS_VIDEO_CREATE']!, video, { headers: headers })
        .catch((error) => {
          throw error;
        })
        .then((response) => {
          return response.data;
        });
    });
  }

  async update(video: Partial<Video>): Promise<Video> {
    return this.utilsService.setAxiosHeaders(true).then(async (headers) => {
      // console.log('VideoService:: update:', headers);
      return axios
        .put(environment['AWS_VIDEO_UPDATE']!, video, { headers: headers })
        .catch((error) => {
          throw error;
        })
        .then((response) => {
          return response.data;
        });
    });
  }

  async delete(id: string): Promise<string> {
    return this.utilsService.setAxiosHeaders(true).then(async (headers) => {
      return axios
        .delete(environment['AWS_VIDEO_DELETE']!, {
          params: { id: id },
          headers: headers,
        })
        .catch((error) => {
          throw error;
        })
        .then((response) => {
          return id;
        });
    });
  }
}
